// Our Company (SAD-548 ID-07): an authorized user corrects which Company
// profile the tenant IS. Reassigning it is a pointer flip, audited in the
// OwnCompanyDesignation trail below — every relationship, rate, adopted
// assessment and contract attribution already recorded stays exactly as it
// is, attributed to the company that earned it. Writes (and the history
// read) are gated server-side on requireCompanySettingsAuthority, mirrored
// here by useTenantAreas().canManageCompanySettings.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraInput } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { useTenantAreas } from '../../../lib/access/useTenantAreas';
import { formatContractDate } from '../../../lib/contractDate';
import { useOwnCompanyDesignations, useSetOwnCompany } from './ourCompanyHooks';
export function OurCompanySection({ companyProfileId, isOwnCompany, companies }) {
    const { canManageCompanySettings } = useTenantAreas();
    const designations = useOwnCompanyDesignations();
    const setOwnCompany = useSetOwnCompany();
    const [reason, setReason] = useState('');
    const nameOf = (id)=>companies.find((c)=>c.id === id)?.name ?? id;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2",
        "data-testid": Testids.deliveryRegistries.companyOurCompanySection,
        children: [
            /*#__PURE__*/ _jsx("div", {
                // specra-allow text-px, arbitrary-length: matches the established Registries section-heading pattern (see CompanyLineageSection.tsx)
                className: "text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground",
                children: "Our Company"
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "Correcting Our Company changes only which company we are — every relationship, rate, adopted assessment and contract attribution already recorded stays exactly as it is, attributed to the company that earned it."
            }),
            /*#__PURE__*/ _jsx("div", {
                "data-testid": Testids.deliveryRegistries.companyOurCompanyCurrentIndicator,
                children: isOwnCompany ? /*#__PURE__*/ _jsx(SpecraBadge, {
                    variant: "info",
                    children: "Current Our Company"
                }) : /*#__PURE__*/ _jsx("span", {
                    className: "text-xs text-muted-foreground",
                    children: "Not currently Our Company"
                })
            }),
            !isOwnCompany && canManageCompanySettings ? /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraInput, {
                        "data-testid": Testids.deliveryRegistries.companyOurCompanyReasonInput,
                        onChange: (e)=>setReason(e.target.value),
                        placeholder: "Reason (optional)",
                        value: reason
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.deliveryRegistries.companyOurCompanySetAction,
                        disabled: setOwnCompany.isPending,
                        onClick: ()=>setOwnCompany.mutate({
                                companyProfileId,
                                reason: reason.trim() || undefined
                            }, {
                                onSuccess: ()=>setReason('')
                            }),
                        size: "sm",
                        children: "Set as Our Company"
                    })
                ]
            }) : null,
            designations.isError ? null : (designations.data ?? []).length === 0 ? /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "No corrections recorded yet."
            }) : (designations.data ?? []).map((d)=>/*#__PURE__*/ _jsxs("div", {
                    className: "rounded-md border border-border/60 p-2 text-[length:var(--fs-ui-caption)]",
                    "data-testid": Testids.deliveryRegistries.companyOurCompanyHistoryRow,
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            className: "font-medium",
                            children: nameOf(d.companyProfileId)
                        }),
                        ' ',
                        /*#__PURE__*/ _jsxs("span", {
                            className: "text-muted-foreground",
                            "data-testid": Testids.deliveryRegistries.companyOurCompanyHistoryDetail,
                            children: [
                                d.previousCompanyProfileId ? `superseded ${nameOf(d.previousCompanyProfileId)} · ` : '',
                                "decided by ",
                                d.decidedByUserId || 'unknown',
                                " on ",
                                formatContractDate(d.decidedAt),
                                d.reason ? ` — ${d.reason}` : ''
                            ]
                        })
                    ]
                }, d.id))
        ]
    });
}
